import React, { useState, useEffect } from 'react';
import { Check, X, MoreHorizontal, CheckCircle, FilePlus, Copy, Trash2, Eye, Edit3 } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { StatusPill } from './BanfDetailDrawer';
import { banfSubject, banfDisplayStatus, banfVendor, banfMatkl, banfBelegart, fmtAmount, waitingSince } from './banfListUtils';

/**
 * BANF-Tabelle (Design-Handoff §3): ein Scroll-Container (x+y), sticky Kopfzeile,
 * konfigurierbare Spalten, runde Auswahl-Checkpoints, EINE Primäraktion je Zeile,
 * ⋯-Menü (fixed, klemmt an Fensterränder), Dichte über rowPy. Zeilenklick öffnet den Drawer.
 * Entwürfe laufen als eigene Zeilenform (isDraft) mit Bearbeiten/Löschen.
 */
const BanfTable = ({
  rows, visibleColumns, density, selection, onToggleSelect, onToggleSelectAll,
  canDecide, canDelete, processing, onOpen, onApprove, onReject, onReorder, onDelete,
  onEditDraft, onDeleteDraft, onResetFilters, openId,
}) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const [menu, setMenu] = useState(null); // { row, x, y, up }

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [menu]);

  const rowPy = density === 'compact' ? 'py-[7px]' : 'py-[13px]';
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US') : '—');

  const headCls = `text-[10.5px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const col = {
    select: 'w-[30px] flex-shrink-0',
    main: 'flex-1 min-w-[200px]',
    status: 'w-[118px] flex-shrink-0',
    amount: 'w-[108px] flex-shrink-0 text-right',
    dept: 'w-[88px] flex-shrink-0',
    anforderer: 'w-[150px] flex-shrink-0',
    kostl: 'w-[124px] flex-shrink-0',
    matkl: 'w-[148px] flex-shrink-0',
    lieferant: 'w-[150px] flex-shrink-0',
    belegart: 'w-[112px] flex-shrink-0',
    erstellt: 'w-[112px] flex-shrink-0',
    sap: 'w-[128px] flex-shrink-0',
    action: 'w-[150px] flex-shrink-0 flex items-center justify-end gap-1.5',
  };
  const show = (k) => visibleColumns.includes(k);

  const selectableRows = rows.filter((r) => !r.isDraft);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selection.includes(r.id));

  const CheckDot = ({ checked, onClick, disabled }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      className={`w-[19px] h-[19px] rounded-full border-2 flex items-center justify-center transition-colors ${
        checked
          ? 'bg-brand border-brand text-white'
          : (isDarkMode ? 'border-white/25 hover:border-white/50' : 'border-slate-300 hover:border-slate-400')
      } ${disabled ? 'opacity-30 cursor-default' : ''}`}
    >
      {checked && <Check className="w-3 h-3" strokeWidth={3} />}
    </button>
  );

  const openMenu = (e, row) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 200;
    const x = Math.min(Math.max(8, r.right - 216), window.innerWidth - 224);
    setMenu({ row, x, y: up ? r.top : r.bottom + 4, up });
  };

  const MenuItem = ({ icon: Icon, label, danger, onClick }) => (
    <button
      type="button"
      onClick={() => { setMenu(null); onClick(); }}
      className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm rounded-full transition-colors text-left ${
        danger
          ? (isDarkMode ? 'text-red-300 hover:bg-red-500/15' : 'text-red-600 hover:bg-red-50')
          : (isDarkMode ? 'text-white/85 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-50')
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className={`relative flex-1 min-h-[300px] rounded-t-3xl border overflow-hidden flex flex-col ${
      isDarkMode ? 'bg-gray-900/40 border-white/15 backdrop-blur-xl' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <div className="flex-1 overflow-auto">
        <div className="min-w-full w-max">
          {/* Kopfzeile */}
          <div className={`sticky top-0 z-10 flex items-center gap-2 px-4 py-2.5 border-b w-full ${
            isDarkMode ? 'bg-gray-900/95 border-white/10' : 'bg-slate-50 border-slate-100'
          }`}>
            <div className={col.select}>
              {canDecide && <CheckDot checked={allSelected} onClick={onToggleSelectAll} disabled={selectableRows.length === 0} />}
            </div>
            <div className={`${col.main} ${headCls}`}>{t('bl.colBanf')}</div>
            <div className={`${col.status} ${headCls}`}>{t('bl.colStatus')}</div>
            <div className={`${col.amount} ${headCls}`}>{t('bl.colAmount')}</div>
            <div className={`${col.dept} ${headCls}`}>{t('bl.colDept')}</div>
            {show('anforderer') && <div className={`${col.anforderer} ${headCls}`}>{t('bl.colAnforderer')}</div>}
            {show('kostl') && <div className={`${col.kostl} ${headCls}`}>{t('bl.colKostl')}</div>}
            {show('matkl') && <div className={`${col.matkl} ${headCls}`}>{t('bl.colMatkl')}</div>}
            {show('lieferant') && <div className={`${col.lieferant} ${headCls}`}>{t('bl.colLieferant')}</div>}
            {show('belegart') && <div className={`${col.belegart} ${headCls}`}>{t('bl.colBelegart')}</div>}
            {show('erstellt') && <div className={`${col.erstellt} ${headCls}`}>{t('bl.colErstellt')}</div>}
            {show('sap') && <div className={`${col.sap} ${headCls}`}>{t('bl.colSap')}</div>}
            <div className={`${col.action} ${headCls} justify-end`}>{t('bl.colAction')}</div>
          </div>

          {/* Zeilen */}
          {rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('bl.empty')}</p>
              <button onClick={onResetFilters} className={`mt-2 text-sm underline ${isDarkMode ? 'text-blue-300' : 'text-brand'}`}>
                {t('bl.resetFilters')}
              </button>
            </div>
          ) : rows.map((r) => {
            const status = r.isDraft ? 'draft' : banfDisplayStatus(r);
            const selected = !r.isDraft && selection.includes(r.id);
            const isOpen = !r.isDraft && openId === r.id;
            return (
              <div
                key={r.isDraft ? `d${r.id}` : r.id}
                onClick={() => (r.isDraft ? onEditDraft(r.raw) : onOpen(r.id))}
                className={`flex items-center gap-2 px-4 ${rowPy} border-b cursor-pointer transition-colors w-full ${
                  isDarkMode
                    ? `border-white/5 ${isOpen || selected ? 'bg-white/5' : 'hover:bg-white/5'}`
                    : `border-slate-50 ${isOpen || selected ? 'bg-slate-50' : 'hover:bg-slate-50'}`
                }`}
              >
                <div className={col.select}>
                  {!r.isDraft && !r.deleted_at && canDecide && <CheckDot checked={selected} onClick={() => onToggleSelect(r.id)} />}
                </div>

                {/* BANF/Betreff */}
                <div className={`${col.main} min-w-0`}>
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className={`font-mono text-xs flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>#{r.id}</span>
                    <span className={`text-[13.5px] font-bold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {r.isDraft ? (r.title || t('bl.stDraft')) : banfSubject(r)}
                    </span>
                  </div>
                  <p className={`text-[11.5px] truncate ${
                    status === 'pending' ? (isDarkMode ? 'text-amber-300' : 'text-amber-600') : (isDarkMode ? 'text-white/45' : 'text-slate-400')
                  }`}>
                    {r.isDraft
                      ? t('bl.draftSub')
                      : status === 'pending'
                        ? `${t('bl.waitingApproval')} · ${waitingSince(r, language)}`
                        : status === 'rejected'
                          ? (r.approver_comment || t('bl.stRejected'))
                          : `${banfVendor(r) || '—'} · ${(r.items || []).length} ${(r.items || []).length === 1 ? t('bl.position') : t('bl.positions')}`}
                  </p>
                </div>

                <div className={col.status}><StatusPill status={status} isDarkMode={isDarkMode} t={t} /></div>
                <div className={`${col.amount} font-mono text-[13px] ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
                  {fmtAmount(r.total_amount, r.currency)}
                </div>
                <div className={`${col.dept} text-[13px] truncate ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`}>
                  {r.isDraft ? '—' : (r.requester_department || '—')}
                </div>
                {show('anforderer') && (
                  <div className={`${col.anforderer} text-[13px] truncate ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                    {r.isDraft ? '—' : (r.requester_name || '—')}
                    {!r.isDraft && r.created_at && (
                      <p className={`text-[11px] ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{fmtDate(r.created_at)}</p>
                    )}
                  </div>
                )}
                {show('kostl') && <div className={`${col.kostl} font-mono text-[12.5px] ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{r.isDraft ? '—' : (r.requester_cost_center || '—')}</div>}
                {show('matkl') && <div className={`${col.matkl} text-[12.5px] truncate ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{r.isDraft ? '—' : (banfMatkl(r) || '—')}</div>}
                {show('lieferant') && <div className={`${col.lieferant} text-[12.5px] truncate ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{r.isDraft ? '—' : (banfVendor(r) || '—')}</div>}
                {show('belegart') && <div className={`${col.belegart} font-mono text-[12.5px] ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{r.isDraft ? '—' : banfBelegart(r)}</div>}
                {show('erstellt') && <div className={`${col.erstellt} text-[12.5px] ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{fmtDate(r.created_at)}</div>}
                {show('sap') && (
                  <div className={col.sap}>
                    {r.sap_transfer_status === 'failed' ? (
                      <span title={r.sap_last_message || ''} className={`text-[12px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600'}`}>
                        {t('bl.sapFailedPill')}
                      </span>
                    ) : r.sap_banfn ? (
                      <span className={`font-mono text-[12px] px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-brand/10 text-brand'}`}>{r.sap_banfn}</span>
                    ) : (
                      <span className={isDarkMode ? 'text-white/30' : 'text-slate-300'}>—</span>
                    )}
                  </div>
                )}

                {/* Aktion */}
                <div className={col.action} onClick={(e) => e.stopPropagation()}>
                  {r.isDraft ? (
                    <>
                      <button onClick={() => onEditDraft(r.raw)}
                        className={`px-3.5 py-1.5 text-[12.5px] font-medium rounded-full border transition-colors ${isDarkMode ? 'border-white/20 text-white/80 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        <Edit3 className="w-3.5 h-3.5 inline mr-1" />{t('bl.edit')}
                      </button>
                      <button onClick={() => onDeleteDraft(r.id)} title={t('pr.deleteDraft')}
                        className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'border-white/15 text-white/50 hover:bg-red-500/20 hover:text-red-200' : 'border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600'}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : status === 'deleted' ? (
                    <>
                      <button onClick={() => onReorder(r)} disabled={processing}
                        className={`px-3.5 py-1.5 text-[12.5px] font-medium rounded-full border transition-colors disabled:opacity-50 ${isDarkMode ? 'border-white/20 text-white/80 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        {t('pr.reorder')}
                      </button>
                      <button onClick={(e) => openMenu(e, r)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </>
                  ) : status === 'pending' && canDecide ? (
                    <>
                      <button onClick={() => onApprove(r.id)} disabled={processing}
                        className="px-3.5 py-1.5 text-[12.5px] font-semibold rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50 flex items-center">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />{t('pr.approve')}
                      </button>
                      <button onClick={() => onReject(r.id)} disabled={processing} title={t('pr.reject')}
                        className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors disabled:opacity-50 ${isDarkMode ? 'border-white/15 text-white/60 hover:bg-red-500/20 hover:text-red-200' : 'border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600'}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => openMenu(e, r)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => onOpen(r.id)}
                        className={`px-3.5 py-1.5 text-[12.5px] font-medium rounded-full transition-colors ${isDarkMode ? 'bg-blue-600/70 text-white hover:bg-blue-600' : 'bg-brand text-white hover:bg-[#1D4ED8]'}`}>
                        {t('bl.details')}
                      </button>
                      <button onClick={(e) => openMenu(e, r)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ⋯-Menü (fixed) */}
      {menu && (
        <>
          <div className="fixed inset-0 z-[65]" onClick={() => setMenu(null)} />
          <div
            className={`fixed z-[66] w-[216px] rounded-3xl border shadow-xl p-1.5 ${
              isDarkMode ? 'bg-gray-900/95 border-white/15 backdrop-blur-xl' : 'bg-white border-slate-200'
            }`}
            style={{ left: menu.x, top: menu.up ? undefined : menu.y, bottom: menu.up ? window.innerHeight - menu.y + 4 : undefined }}
          >
            <MenuItem icon={Eye} label={t('bl.showDetails')} onClick={() => onOpen(menu.row.id)} />
            <MenuItem icon={FilePlus} label={t('pr.reorder')} onClick={() => onReorder(menu.row)} />
            {menu.row.sap_banfn && (
              <MenuItem icon={Copy} label={t('bl.copySap')} onClick={() => { navigator.clipboard?.writeText(menu.row.sap_banfn).catch(() => {}); }} />
            )}
            {canDelete && !menu.row.deleted_at && (
              <>
                <div className={`my-1 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`} />
                <MenuItem icon={Trash2} danger label={t('pr.deleteRequest')} onClick={() => onDelete(menu.row)} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BanfTable;
